import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import apiConfig from "../../config/apiConfig";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { fetchForeignResource } from "../../apis/resources";
import { fetchEnum } from "../../apis/enum";
import { useRoundsViewModel } from "../../viewModels/useRoundsViewModel";
import { LoginContext } from "../../context/LoginContext";
import { apiFetch } from "../../apis/apiFetch";
import styles from "../Styles/CreateCertificate.module.css";
import DocumentPreview from "../Common/DocumentPreview";
import { Application } from "./ViewRounds";
import { toast as toastUtil } from "../../utils/toast";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────
async function safeReadJson(res: Response) {
  const text = await res.text();
  if (!text) return null;
  try { return JSON.parse(text); } catch { return null; }
}

const PREV_STATUS_OPTIONS = [
  { label: "Offer Sent", value: "OFFER_SENT" },
  { label: "Accepted", value: "ACCEPTED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Withdrawn", value: "WITHDRAWN" },
  { label: "Expired", value: "EXPIRED" },
  { label: "Offer Cancelled", value: "OFFER_CANCELLED" },
  { label: "Offer Generated", value: "OFFER_GENERATED" },
  { label: "Offer Confirmed", value: "OFFER_CONFIRMED" },
];

const ALL_PREV_STATUS_VALUES = PREV_STATUS_OPTIONS.map((o) => o.value);

const formatDateDDMMYYYY = (dateValue: any) => {
  if (!dateValue) return "No Date";
  const d = new Date(dateValue);
  if (isNaN(d.getTime())) return "No Date";
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

// ──────────────────────────────────────────────────────────────────────────────
const CreateRounds = () => {
  const { user } = useContext(LoginContext);
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);


  const [gateCounts, setGateCounts] = useState<any>({
    cse_count: 0, ece_count: 0, ai_count: 0,
    cse_cutoff: null, ece_cutoff: null, ai_cutoff: null,
  });

  // Previous-round selection state
  const [prevRoundId, setPrevRoundId] = useState<string>("");
  const [prevRoundMode, setPrevRoundMode] = useState<"INCLUDE" | "EXCLUDE">("INCLUDE");
  const [prevStatuses, setPrevStatuses] = useState<string[]>(["ACCEPTED", "REJECTED", "WITHDRAWN", "EXPIRED"]);
  // B.Tech Offline Round Mode
  const [allocationMode, setAllocationMode] = useState<"" | "RECOVERY" | "SLIDING">(""); // "" = Fresh (default)
  const [parentRoundId, setParentRoundId] = useState<string>("");

  const fetchedResources = useRef(new Set<string>());
  const fetchedEnum = useRef(new Set<string>());
  const queryClient = useQueryClient();

  const { appId }: any = useParams<any>();

  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  // Pagination State
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const totalCount = applications.length;

  const [selectedApplications, setSelectedApplications] = useState<Record<string, string>>({});

  const handleAppSelection = (appNo: string, checked: boolean) => {
    // Remove preference when app is deselected
    if (!checked) {
      setSelectedApplications((prev) => {
        const updated = { ...prev };
        delete updated[appNo];
        return updated;
      });
    }
  };

  const getpref = (name: string) => {
    if (name === "B.Tech. (Computer Science and Engineering)") return "BTech_CSE";
    if (name === "B.Tech. (Electronics and Communication Engineering)") return "BTech_ECE";
    if (name === "B.Tech. (Artificial Intelligence & Data Science)") return "BTech_AI_DS";
    if (name === "Integrated M.Tech (Computer Science and Engineering)") return "I_MTech_CSE";
    if (name === "Integrated M.Tech. (Electronics and Communication Engineering)") return "I_MTech_ECE";
  }
  const handlePreferenceSelect = (appNo: string, preference: string) => {
    setSelectedApplications((prev) => ({
      ...prev,
      [appNo]: preference
      // BTech_CSE, BTech_ECE, BTech_AI_DS,IMTech_CSE,IMTech_ECE
    }));
  };

  const {
    fields,
    setFields,
    enums,
    setEnums,
    setForeignKeyData,
    dataToSave,
    setDataToSave,
    loadMetadata,
    save,
  } = useRoundsViewModel(user?.userId || "", appId);

  useEffect(() => { loadMetadata(); }, []);

  // ── Metadata ──────────────────────────────────────────────────────────────
  useQuery({
    queryKey: ["resMetaData", "roundsCreate"],
    queryFn: async () => {
      const res = await apiFetch(apiConfig.getResourceMetaDataUrl("Rounds"));
      const data = await res.json();
      setFields(data?.[0]?.fieldValues || []);

      for (const f of data?.[0]?.fieldValues || []) {
        if (f.foreign && !fetchedResources.current.has(f.foreign)) {
          fetchedResources.current.add(f.foreign);
          const d = await fetchForeignResource(f.foreign);
          setForeignKeyData((p: any) => ({ ...p, [f.foreign]: d }));
        }
        if (f.isEnum && !fetchedEnum.current.has(f.possible_value)) {
          fetchedEnum.current.add(f.possible_value);
          const e = await fetchEnum(f.possible_value);
          setEnums((p: any) => ({ ...p, [f.possible_value]: e }));
        }
      }
      return data;
    },
  });

  // ── Drive options ─────────────────────────────────────────────────────────
  const { data: driveOptions } = useQuery({
    queryKey: ["driveOptions", "roundsCreate"],
    queryFn: async () => {
      const res = await apiFetch(`${apiConfig.getResourceUrl("drive")}?queryId=GET_ALL`, { method: "GET" });
      const body = await safeReadJson(res);
      return body?.resource || [];
    },
  });

  const drives = useMemo(
    () => (Array.isArray(driveOptions) ? driveOptions.filter((d) => d.archived !== "Y") : []),
    [driveOptions]
  );

  // ── All rounds (for "prev round" dropdown) ────────────────────────────────
  const { data: allRoundsData } = useQuery({
    queryKey: ["allRounds", "roundsCreate"],
    queryFn: async () => {
      const res = await apiFetch(`${apiConfig.getResourceUrl("rounds")}?queryId=GET_ALL`);
      if (!res.ok) return [];
      const body = await safeReadJson(res);
      return body?.resource || [];
    },
  });

  // Rounds for the same drive, sorted oldest→newest (no current-round to exclude on create)
  const prevRoundOptions = useMemo(() => {
    if (!Array.isArray(allRoundsData) || !dataToSave.drive_id) return [];
    return allRoundsData
      .filter((r: any) => r.drive_id === dataToSave.drive_id && r.round_type?.toUpperCase() === 'OFFLINE')
      .sort((a: any, b: any) => {
        const parse = (v: any) => { const d = new Date(v); return isNaN(d.getTime()) ? Number(v) || 0 : d.getTime(); };
        return parse(a.date) - parse(b.date);
      });
  }, [allRoundsData, dataToSave.drive_id]);

  // When drive changes and there are previous rounds, auto-select the last one
  useEffect(() => {
    if (prevRoundOptions.length > 0) {
      setPrevRoundId(prevRoundOptions[prevRoundOptions.length - 1].id);
    } else {
      setPrevRoundId("");
    }
  }, [prevRoundOptions.length, dataToSave.drive_id]);

  // ── Generate cutoffs ──────────────────────────────────────────────────────
  const fetchCounts = async () => {
    const selectedDrive = drives.find((d: any) => d.id === dataToSave.drive_id);
    if (selectedDrive?.drivetype !== "Mtech" && selectedDrive?.drivetype !== "Btech") return;

    const argsArr: string[] = [];
    if (dataToSave.drive_id) argsArr.push(`drive_id:${dataToSave.drive_id}`);
    if (dataToSave.allocation_type) argsArr.push(`allocation_type:${dataToSave.allocation_type}`);

    if (dataToSave.cse_seats) argsArr.push(`cse_seats:${dataToSave.cse_seats}`);
    if (dataToSave.ece_seats) argsArr.push(`ece_seats:${dataToSave.ece_seats}`);
    if (dataToSave.ai_seats) argsArr.push(`ai_seats:${dataToSave.ai_seats}`);

    // B.Tech
    if (dataToSave.btech_cse_seats) argsArr.push(`btech_cse_seats:${dataToSave.btech_cse_seats}`);
    if (dataToSave.btech_ece_seats) argsArr.push(`btech_ece_seats:${dataToSave.btech_ece_seats}`);
    if (dataToSave.btech_ai_seats) argsArr.push(`btech_ai_seats:${dataToSave.btech_ai_seats}`);
    if (dataToSave.imtech_cse_seats) argsArr.push(`imtech_cse_seats:${dataToSave.imtech_cse_seats}`);
    if (dataToSave.imtech_ece_seats) argsArr.push(`imtech_ece_seats:${dataToSave.imtech_ece_seats}`);

    if (dataToSave.cse_gate_min) argsArr.push(`cse_gate_min:${dataToSave.cse_gate_min}`);
    if (dataToSave.cse_gate_max) argsArr.push(`cse_gate_max:${dataToSave.cse_gate_max}`);
    if (dataToSave.ece_gate_min) argsArr.push(`ece_gate_min:${dataToSave.ece_gate_min}`);
    if (dataToSave.ece_gate_max) argsArr.push(`ece_gate_max:${dataToSave.ece_gate_max}`);
    if (dataToSave.ai_gate_min) argsArr.push(`ai_gate_min:${dataToSave.ai_gate_min}`);
    if (dataToSave.ai_gate_max) argsArr.push(`ai_gate_max:${dataToSave.ai_gate_max}`);

    if (prevRoundId) {
      argsArr.push(`prev_round_id:${prevRoundId}`);
      argsArr.push(`prev_round_mode:${prevRoundMode}`);
      // Use | as separator within the value so it doesn't clash with , which separates args
      argsArr.push(`prev_statuses:${prevStatuses.join("|")}`)
    }

    // B.Tech Recovery / Sliding mode
    if ((allocationMode === "RECOVERY" || allocationMode === "SLIDING") && parentRoundId) {
      argsArr.push(`allocation_mode:${allocationMode}`);
      argsArr.push(`parent_round_id:${parentRoundId}`);
    }

    // Always force recompute when explicitly clicking "Generate Cutoffs"
    argsArr.push("force_recompute:true");

    const params = new URLSearchParams({ queryId: "GET_GATE_COUNTS" });
    if (argsArr.length > 0) params.append("args", argsArr.join(","));

    try {
      const res = await apiFetch(`${apiConfig.getResourceUrl("applications")}?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        if (data?.resource?.[0]) {
          const root = data.resource[0];
          setGateCounts(root);
          setDataToSave((prev: any) => ({
            ...prev,
            cse_cutoff: root.cse_cutoff,
            ece_cutoff: root.ece_cutoff,
            ai_cutoff: root.ai_cutoff,
            cse_count: root.cse_count,
            ece_count: root.ece_count,
            ai_count: root.ai_count,
            // B.Tech
            btech_cse_cutoff: root.btech_cse_cutoff,
            btech_ece_cutoff: root.btech_ece_cutoff,
            btech_ai_cutoff: root.btech_ai_cutoff,
            imtech_cse_cutoff: root.imtech_cse_cutoff,
            imtech_ece_cutoff: root.imtech_ece_cutoff,
            btech_cse_count: root.btech_cse_count,
            btech_ece_count: root.btech_ece_count,
            btech_ai_count: root.btech_ai_count,
            imtech_cse_count: root.imtech_cse_count,
            imtech_ece_count: root.imtech_ece_count,
          }));
          setToast({ type: "success", msg: "Cutoffs generated successfully!" });
        }
      }
    } catch (err) { }
  };

  const togglePrevStatus = (status: string) => {
    setPrevStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const toggleAllPrevStatuses = () => {
    setPrevStatuses((prev) =>
      prev.length === ALL_PREV_STATUS_VALUES.length ? [] : [...ALL_PREV_STATUS_VALUES]
    );
  };

  // ── Create ────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    try {
      if (!dataToSave.name) return setToast({ type: "error", msg: "Name is required" });
      if (!dataToSave.drive_id) return setToast({ type: "error", msg: "Drive is required" });
      if (!dataToSave.startdate) return setToast({ type: "error", msg: "Start Date is required" });
      if (!dataToSave.enddate) return setToast({ type: "error", msg: "End Date is required" });
      if (!dataToSave.round_type) return setToast({ type: "error", msg: "Round Type is required" });
      if (!dataToSave.details) return setToast({ type: "error", msg: "Details are required" });
      if (!dataToSave.offer_letter_template_doc_id) return setToast({ type: "error", msg: "Template is required" });

      const selectedDrive = drives.find((d: any) => d.id === dataToSave.drive_id);
      if (selectedDrive?.drivetype === "Mtech") {
        const allocType = dataToSave.allocation_type || "SEAT_BASED";
        if (allocType === "SEAT_BASED") {
          const cseField = isOnline ? "mtech_online_cse_seats" : "cse_seats";
          const eceField = isOnline ? "mtech_online_ece_seats" : "ece_seats";
          const aiField = isOnline ? "mtech_online_aids_seats" : "ai_seats";
          const hasCse = dataToSave[cseField] !== undefined && dataToSave[cseField] !== "";
          const hasEce = dataToSave[eceField] !== undefined && dataToSave[eceField] !== "";
          const hasAi = dataToSave[aiField] !== undefined && dataToSave[aiField] !== "";

          if (!hasCse && !hasEce && !hasAi) {
            return setToast({ type: "error", msg: "Please provide at least one Available Seats limit for M.Tech programs." });
          }
          const hasGenerated = ('cse_cutoff' in dataToSave) || ('ece_cutoff' in dataToSave) || ('ai_cutoff' in dataToSave);
          if (!hasGenerated) {
            const msg = isOnline ? "Please enter at least one Cutoff before submitting" : "Please click 'Generate Cutoffs' before submitting";
            return setToast({ type: "error", msg });
          }
        } else {
          const hasCse = dataToSave.cse_gate_min !== undefined && dataToSave.cse_gate_min !== "";
          const hasEce = dataToSave.ece_gate_min !== undefined && dataToSave.ece_gate_min !== "";
          const hasAi = dataToSave.ai_gate_min !== undefined && dataToSave.ai_gate_min !== "";

          if (!hasCse && !hasEce && !hasAi) {
            return setToast({ type: "error", msg: "Please provide at least one Cutoff Range for M.Tech programs." });
          }
          if (dataToSave.cse_gate_min && gateCounts.cse_count == null) {
            return setToast({ type: "error", msg: "Please click 'Generate Counts' before submitting" });
          }
        }
        executeCreate();
      } else if (selectedDrive?.drivetype === "Btech" && !isOnline && !isManual) {
        const allocType = dataToSave.allocation_type || "SEAT_BASED";
        if (allocType === "SEAT_BASED") {
          const hasBtechCse = dataToSave.btech_cse_seats !== undefined && dataToSave.btech_cse_seats !== "";
          const hasBtechEce = dataToSave.btech_ece_seats !== undefined && dataToSave.btech_ece_seats !== "";
          const hasBtechAi = dataToSave.btech_ai_seats !== undefined && dataToSave.btech_ai_seats !== "";
          const hasImtechCse = dataToSave.imtech_cse_seats !== undefined && dataToSave.imtech_cse_seats !== "";
          const hasImtechEce = dataToSave.imtech_ece_seats !== undefined && dataToSave.imtech_ece_seats !== "";

          if (allocationMode !== "RECOVERY" && !hasBtechCse && !hasBtechEce && !hasBtechAi && !hasImtechCse && !hasImtechEce) {
            return setToast({ type: "error", msg: "Please provide at least one Available Seats limit for B.Tech/IM.Tech programs." });
          }
          const hasAnyCutoff = !!(dataToSave.btech_cse_cutoff || dataToSave.btech_ece_cutoff || dataToSave.btech_ai_cutoff || dataToSave.imtech_cse_cutoff || dataToSave.imtech_ece_cutoff);
          if (allocationMode === "RECOVERY" && !hasAnyCutoff) {
            return setToast({ type: "error", msg: "Please click 'Generate Cutoffs' before submitting" });
          }
          if (allocationMode !== "RECOVERY" && (hasBtechCse || hasBtechEce || hasBtechAi || hasImtechCse || hasImtechEce) && !hasAnyCutoff) {
            return setToast({ type: "error", msg: "Please click 'Generate Cutoffs' before submitting" });
          }
        }
        executeCreate();
      } else if (selectedDrive?.drivetype === "Btech" && isOnline && !isManual) {
        const allocType = dataToSave.allocation_type || "SEAT_BASED";
        if (allocType === "SEAT_BASED") {
          const has_btech_online_cse = dataToSave.btech_online_cse_seats !== undefined && dataToSave.btech_online_cse_seats !== "";
          const has_btech_online_ece = dataToSave.btech_online_ece_seats !== undefined && dataToSave.btech_online_ece_seats !== "";
          const has_btech_online_aids = dataToSave.btech_online_aids_seats !== undefined && dataToSave.btech_online_aids_seats !== "";
          const has_imtech_online_cse = dataToSave.imtech_online_cse_seats !== undefined && dataToSave.imtech_online_cse_seats !== "";
          const has_imtech_online_ece = dataToSave.imtech_online_ece_seats !== undefined && dataToSave.imtech_online_ece_seats !== "";

          if (!isOnline && !has_btech_online_cse && !has_btech_online_ece && !has_btech_online_aids && !has_imtech_online_cse && !has_imtech_online_ece) {
            return setToast({ type: "error", msg: "Please provide at least one Available Seats limit for B.Tech programs." });
          }

          if (!dataToSave.btech_cse_cutoff && !dataToSave.btech_ece_cutoff && !dataToSave.btech_ai_cutoff && !dataToSave.imtech_cse_cutoff && !dataToSave.imtech_ece_cutoff) {
            return setToast({ type: "error", msg: "Please enter Cutoffs' before submitting" });
          }

        }
        executeCreate();
      } else {
        if (selectedApplications === null || Object.keys(selectedApplications).length <= 0) {
          return setToast({ type: "error", msg: "Please select applications before submitting" });
        }
        executeCreate();
      }
    } catch (e: any) {
      setToast({ type: "error", msg: e?.message || "Validation failed" });
    }
  };

  const fetchApplications = async (driveId: string) => {
    setLoading(true);
    setApplications([]);
    setPageNo(1)
    setPageSize(20)
    setSelectedApplications({})
    try {
      const url = `${apiConfig.getResourceUrl("applications")}?queryId=GET_APPLICATIONS_BY_DRIVE&args=${encodeURIComponent(`drive_id:${driveId}`)}`;
      const response = await apiFetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();

      let apps = [];
      if (Array.isArray(data)) apps = data;
      else if (data && Array.isArray(data.resource)) apps = data.resource;

      setApplications(apps as Application[]);
      if (apps.length === 0) {
        toastUtil.info("No eligible applications found for this round.");
      }
    } catch (err) {
      console.error("ViewRounds: Error fetching applications", err);
      toastUtil.error("Error fetching applications for this round.");
    } finally {
      setLoading(false);
    }
  };

  const executeCreate = async () => {
    try {
      // Store prev-round selection in extra_data so GET_ROUND_APPLICATIONS can use it
      const payload: any = { ...dataToSave };

      const selectedDrive = drives.find((d: any) => d.id === dataToSave.drive_id);
      if (selectedDrive?.drivetype === "Mtech" || selectedDrive?.drivetype === "Btech") {
        payload.allocation_locked = "Y";
      }


      // Sanitize numeric fields: convert "" to null to avoid NumberFormatException on backend
      const numericFields = [
        'cse_seats', 'ece_seats', 'ai_seats',
        'cse_cutoff', 'ece_cutoff', 'ai_cutoff',
        'cse_gate_min', 'cse_gate_max', 'ece_gate_min', 'ece_gate_max', 'ai_gate_min', 'ai_gate_max',
        'cse_count', 'ece_count', 'ai_count',
        'btech_cse_seats', 'btech_ece_seats', 'btech_ai_seats', 'imtech_cse_seats', 'imtech_ece_seats',
        'btech_cse_cutoff', 'btech_ece_cutoff', 'btech_ai_cutoff', 'imtech_cse_cutoff', 'imtech_ece_cutoff',
        'btech_cse_count', 'btech_ece_count', 'btech_ai_count', 'imtech_cse_count', 'imtech_ece_count'
      ];

      numericFields.forEach(field => {
        if (payload[field] === "") {
          payload[field] = null;
        }
      });

      // Clear fields of the inactive strategy
      if (payload.allocation_type === "CUTOFF_BASED") {
        payload.cse_seats = null;
        payload.ece_seats = null;
        payload.ai_seats = null;
        payload.cse_cutoff = null;
        payload.ece_cutoff = null;
        payload.ai_cutoff = null;
      } else {
        payload.cse_gate_min = null;
        payload.cse_gate_max = null;
        payload.ece_gate_min = null;
        payload.ece_gate_max = null;
        payload.ai_gate_min = null;
        payload.ai_gate_max = null;
      }

      if (prevRoundId) {
        payload.prev_round_id = prevRoundId;
        payload.prev_round_mode = prevRoundMode;
        payload.prev_statuses = prevStatuses.join(",");
      } else {
        payload.prev_round_id = null;
        payload.prev_round_mode = null;
        payload.prev_statuses = null;
      }

      // B.Tech Offline allocation mode
      if ((allocationMode === "RECOVERY" || allocationMode === "SLIDING") && parentRoundId) {
        payload.allocation_mode = allocationMode;
        payload.prev_round_id   = parentRoundId; // store parent in prev_round_id for backend
        payload.prev_round_mode = "EXCLUDE";     // recovery always excludes parent allocations
        payload.prev_statuses = null;
      } else {
        payload.allocation_mode = allocationMode || null;
      }
      const extraData = {
        "applications": Object.entries(selectedApplications).map(
          ([appNo, preference]) => ({
            appNo,
            preference: getpref(preference),
          })
        )
      }
      payload.extra_data = extraData
      console.log("payload", payload)
      await save(payload);
      setToast({ type: "success", msg: "Created successfully!" });
      setDataToSave({});
      setPrevRoundId("");
      setPrevRoundMode("INCLUDE");
      setPrevStatuses(["ACCEPTED", "REJECTED", "WITHDRAWN", "EXPIRED"]);
      setAllocationMode("");
      setParentRoundId("");
      queryClient.invalidateQueries({ queryKey: ["resourceData", "roundsCreate"] });
    } catch (e: any) {
      setToast({ type: "error", msg: e?.message || "Create failed" });
    }
  };

  const isMtech = drives.find((d: any) => d.id === dataToSave.drive_id)?.drivetype === "Mtech";
  const isBtech = drives.find((d: any) => d.id === dataToSave.drive_id)?.drivetype === "Btech";
  const isOnline = dataToSave.round_type === "Online";
  const isManual = dataToSave.round_type === "Manual";

  const columnDefs: ColDef[] = [
    {
      headerName: "",
      width: 70,
      cellRenderer: (params: any) => (
        <input
          type="checkbox"
          checked={!!selectedApplications[params.data.application_number]}
          onChange={(e) =>
            handleAppSelection(
              params.data.application_number,
              e.target.checked
            )
          }
        />
      ),
    },
    {
      headerName: "#",
      width: 80,
      valueGetter: (params: any) =>
        (pageNo - 1) * pageSize + params.node.rowIndex + 1,
    },
    {
      headerName: "Application No.",
      field: "application_number",
      valueFormatter: (params: any) => params.value || "—",
      minWidth: 170,
    },
    {
      headerName: "User Name",
      field: "fullname",
      valueFormatter: (params: any) => params.value || "—",
      minWidth: 200,
    },
    {
      headerName: "Pref 1",
      field: "pref1",
      minWidth: 180,
      cellRenderer: (params: any) => (
        <>
          <input
            type="radio"
            name={`pref-${params.data.application_number}`}
            checked={
              params.value
                ? selectedApplications[params.data.application_number] ===
                params.value
                : false
            }
            onChange={() =>
              handlePreferenceSelect(
                params.data.application_number,
                params.value || ""
              )
            }
          />{" "}
          {params.value || "NA"}
        </>
      ),
    },
    {
      headerName: "Pref 2",
      field: "pref2",
      minWidth: 180,
      cellRenderer: (params: any) => (
        <>
          <input
            type="radio"
            name={`pref-${params.data.application_number}`}
            checked={
              params.value
                ? selectedApplications[params.data.application_number] ===
                params.value
                : false
            }
            onChange={() =>
              handlePreferenceSelect(
                params.data.application_number,
                params.value || ""
              )
            }
          />{" "}
          {params.value || "NA"}
        </>
      ),
    },
    {
      headerName: "Pref 3",
      field: "pref3",
      minWidth: 180,
      cellRenderer: (params: any) => (
        <>
          <input
            type="radio"
            name={`pref-${params.data.application_number}`}
            checked={
              params.value
                ? selectedApplications[params.data.application_number] ===
                params.value
                : false
            }
            onChange={() =>
              handlePreferenceSelect(
                params.data.application_number,
                params.value || ""
              )
            }
          />{" "}
          {params.value || "NA"}
        </>
      ),
    },
    {
      headerName: "Pref 4",
      field: "pref4",
      minWidth: 180,
      cellRenderer: (params: any) => (
        <>
          <input
            type="radio"
            name={`pref-${params.data.application_number}`}
            checked={
              params.value
                ? selectedApplications[params.data.application_number] ===
                params.value
                : false
            }
            onChange={() =>
              handlePreferenceSelect(
                params.data.application_number,
                params.value || ""
              )
            }
          />{" "}
          {params.value || "NA"}
        </>
      ),
    },
    {
      headerName: "Pref 5",
      field: "pref5",
      minWidth: 180,
      cellRenderer: (params: any) => (
        <>
          <input
            type="radio"
            name={`pref-${params.data.application_number}`}
            checked={
              params.value
                ? selectedApplications[params.data.application_number] ===
                params.value
                : false
            }
            onChange={() =>
              handlePreferenceSelect(
                params.data.application_number,
                params.value || ""
              )
            }
          />{" "}
          {params.value || "NA"}
        </>
      ),
    },
    {
      headerName: "Offer Status",
      field: "offer_status",
      minWidth: 150,
      cellRenderer: (params: any) => (
        <span className="fw-bold">{params.value || "—"}</span>
      ),
    },
    {
      headerName: "Offer Payment Status",
      field: "offered_payment_status",
      minWidth: 150,
      cellRenderer: (params: any) => (
        <span className="fw-bold">{params.value || "—"}</span>
      ),
    },

  ];

  return (
    <div className={styles.batchformCard}>
      <div className={styles.certificateFormWrapper}>
        <h2 className={styles.sectionTitle}>Create Rounds</h2>

        <div className={styles.formGrid}>
          {/* Name */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}><span className={styles.required}>*</span> Name</label>
            <input
              className={styles.formControl}
              value={dataToSave.name || ""}
              onChange={(e) => setDataToSave({ ...dataToSave, name: e.target.value })}
            />
          </div>

          {/* Drive */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}><span className={styles.required}>*</span> Drive</label>
            <select
              className={styles.formControl}
              value={dataToSave.drive_id || ""}
              onChange={(e) => setDataToSave({ ...dataToSave, drive_id: e.target.value })}
            >
              <option value="">Select Drive</option>
              {drives.map((d: any) => (
                <option key={d.id} value={d.id}>{d.name ?? d.id}</option>
              ))}
            </select>
          </div>

          {/* Start Date */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <span className={styles.required}>*</span> Start Date
            </label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD-MM-YYYY"
                value={dataToSave.startdate ? dayjs(dataToSave.startdate) : null}
                onChange={(newValue) => {
                  setDataToSave((prev: any) => ({
                    ...prev,
                    startdate: newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : ""
                  }));
                }}
                slotProps={{
                  textField: {
                    placeholder: "dd-mm-yyyy",
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        padding: '0px 14px',
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        "& fieldset": {
                          borderColor: '#d0d0d0',
                          borderWidth: '1px',
                          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                        },
                        "&:hover fieldset": { borderColor: '#d0d0d0' },
                        "&.Mui-focused fieldset": {
                          borderColor: '#2196f3',
                          boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.1)',
                          borderWidth: '1px'
                        }
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: '10px 0px',
                        fontSize: '14px',
                        color: '#333',
                        fontFamily: 'inherit',
                        height: 'auto',
                      }
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </div>

          {/* End Date */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <span className={styles.required}>*</span> End Date
            </label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD-MM-YYYY"
                value={dataToSave.enddate ? dayjs(dataToSave.enddate) : null}
                onChange={(newValue) => {
                  setDataToSave((prev: any) => ({
                    ...prev,
                    enddate: newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : ""
                  }));
                }}
                slotProps={{
                  textField: {
                    placeholder: "dd-mm-yyyy",
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        padding: '0px 14px',
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        "& fieldset": {
                          borderColor: '#d0d0d0',
                          borderWidth: '1px',
                          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                        },
                        "&:hover fieldset": { borderColor: '#d0d0d0' },
                        "&.Mui-focused fieldset": {
                          borderColor: '#2196f3',
                          boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.1)',
                          borderWidth: '1px'
                        }
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: '10px 0px',
                        fontSize: '14px',
                        color: '#333',
                        fontFamily: 'inherit',
                        height: 'auto',
                      }
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </div>

          {/* Withdrawal End Date */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              Withdrawal End Date
            </label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD-MM-YYYY"
                value={dataToSave.withdrawal_end_date ? dayjs(dataToSave.withdrawal_end_date) : null}
                onChange={(newValue) => {
                  setDataToSave((prev: any) => ({
                    ...prev,
                    withdrawal_end_date: newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : ""
                  }));
                }}
                slotProps={{
                  textField: {
                    placeholder: "dd-mm-yyyy",
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        padding: '0px 14px',
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        "& fieldset": {
                          borderColor: '#d0d0d0',
                          borderWidth: '1px',
                          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                        },
                        "&:hover fieldset": { borderColor: '#d0d0d0' },
                        "&.Mui-focused fieldset": {
                          borderColor: '#2196f3',
                          boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.1)',
                          borderWidth: '1px'
                        }
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: '10px 0px',
                        fontSize: '14px',
                        color: '#333',
                        fontFamily: 'inherit',
                        height: 'auto',
                      }
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </div>

          {/* Registration End Date (Online Rounds Only) */}
          {dataToSave.round_type === "Online" && (
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                <span className={styles.required}>*</span> Registration End Date
              </label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  format="DD-MM-YYYY"
                  value={dataToSave.registration_end_date ? dayjs(dataToSave.registration_end_date) : null}
                  onChange={(newValue) => {
                    setDataToSave((prev: any) => ({
                      ...prev,
                      registration_end_date: newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : ""
                    }));
                  }}
                  slotProps={{
                    textField: {
                      placeholder: "dd-mm-yyyy",
                      fullWidth: true,
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          padding: '0px 14px',
                          backgroundColor: '#fff',
                          borderRadius: '4px',
                          "& fieldset": {
                            borderColor: '#d0d0d0',
                            borderWidth: '1px',
                            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                          },
                          "&:hover fieldset": { borderColor: '#d0d0d0' },
                          "&.Mui-focused fieldset": {
                            borderColor: '#2196f3',
                            boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.1)',
                            borderWidth: '1px'
                          }
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: '10px 0px',
                          fontSize: '14px',
                          color: '#333',
                          fontFamily: 'inherit',
                          height: 'auto',
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </div>
          )}

          {/* Round Type */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}><span className={styles.required}>*</span> Round Type</label>
            <select
              className={styles.formControl}
              value={dataToSave.round_type || ""}
              onChange={(e) => setDataToSave({ ...dataToSave, round_type: e.target.value })}
            >
              <option value="">Select Round Type</option>
              {(enums["RoundType"] || []).map((e: any, idx: number) => (
                <option key={idx} value={e}>{e}</option>
              ))}
            </select>
          </div>

          {/* Round Number (For Online Rounds) */}
          {dataToSave.round_type === "Online" && (
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Round Number</label>
              <select
                className={styles.formControl}
                value={dataToSave.round_number || ""}
                onChange={(e) => setDataToSave({ ...dataToSave, round_number: e.target.value ? Number(e.target.value) : null })}
              >
                <option value="">Select Round Number</option>
                {[1, 2, 3, 4, 5].map((num) => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>
          )}

          {/* Details */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}><span className={styles.required}>*</span> Details</label>
            <input
              className={styles.formControl}
              value={dataToSave.details || ""}
              onChange={(e) => setDataToSave({ ...dataToSave, details: e.target.value })}
            />
          </div>

          {/* Round Fee */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}><span className={styles.required}>*</span> Fee</label>
            <input
              className={styles.formControl}
              type="number"
              value={dataToSave.fee || ""}
              onChange={(e) => setDataToSave({ ...dataToSave, fee: e.target.value })}
            />
          </div>

          {/* Template Name */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Template Name</label>
            <input
              className={styles.formControl}
              value={dataToSave.offer_letter_template_name || ""}
              onChange={(e) => setDataToSave({ ...dataToSave, offer_letter_template_name: e.target.value })}
              placeholder="e.g. M.Tech Offer Letter R1"
            />
          </div>

          {/* ── M.Tech section ─────────────────────────────────────────────── */}
          {isMtech && (
            <div className="col-12 mt-3 p-3 bg-light border rounded" style={{ gridColumn: "1 / -1" }}>
              <h5 className="mb-3">Allocation Strategy</h5>

              {!isOnline && (
                <div className="mb-4">
                  <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" name="allocation_type" id="allocSeat" value="SEAT_BASED"
                      checked={dataToSave.allocation_type !== "CUTOFF_BASED"}
                      onChange={() => setDataToSave({ ...dataToSave, allocation_type: "SEAT_BASED", cse_gate_min: "", cse_gate_max: "", ece_gate_min: "", ece_gate_max: "", ai_gate_min: "", ai_gate_max: "" })} />
                    <label className="form-check-label" htmlFor="allocSeat">Seat Based Allocation</label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" name="allocation_type" id="allocCutoff" value="CUTOFF_BASED"
                      checked={dataToSave.allocation_type === "CUTOFF_BASED"}
                      onChange={() => setDataToSave({ ...dataToSave, allocation_type: "CUTOFF_BASED", cse_seats: "", ece_seats: "", ai_seats: "" })} />
                    <label className="form-check-label" htmlFor="allocCutoff">Cutoff Range Based Allocation</label>
                  </div>
                </div>
              )}

              {dataToSave.allocation_type !== "CUTOFF_BASED" ? (
                <>
                  {/* Seat rows */}
                  {[
                    { label: "M.Tech CSE", field: isOnline ? "mtech_online_cse_seats" : "cse_seats", countKey: "cse_count", cutoffKey: "cse_cutoff" },
                    { label: "M.Tech ECE", field: isOnline ? "mtech_online_ece_seats" : "ece_seats", countKey: "ece_count", cutoffKey: "ece_cutoff" },
                    { label: "M.Tech AI&DS", field: isOnline ? "mtech_online_aids_seats" : "ai_seats", countKey: "ai_count", cutoffKey: "ai_cutoff" },
                  ].map(({ label, field, countKey, cutoffKey }) => (
                    <div className="row mb-3" key={field}>
                      <div className="col-md-2 fw-bold d-flex align-items-center">{label}</div>
                      <div className="col-md-4">
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Available Seats"
                          value={(dataToSave as any)[field] || ""}
                          onChange={(e) => setDataToSave({ ...dataToSave, [field]: e.target.value })}
                        />
                      </div>
                      <div className="col-md-6 d-flex align-items-center gap-2 text-primary">
                        {isOnline ? (
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Cutoff"
                            value={(dataToSave as any)[cutoffKey] || ""}
                            onChange={(e) => setDataToSave({ ...dataToSave, [cutoffKey]: e.target.value })}
                            style={{ maxWidth: "150px" }}
                          />
                        ) : (
                          <>
                            {gateCounts[cutoffKey] != null && (
                              <span className="badge bg-success">Cutoff: {gateCounts[cutoffKey]}</span>
                            )}
                            {gateCounts[countKey] > 0 && (
                              <span>Allocating {gateCounts[countKey]} candidates</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  {/* Cutoff Range Rows */}
                  <div className="row mb-3">
                    <div className="col-md-3 fw-bold d-flex align-items-center">M.Tech CSE <span className="badge bg-primary ms-2">{gateCounts.cse_count ?? 0} Applications</span></div>
                    <div className="col-md-3">
                      <input type="number" className="form-control" placeholder="Min Score" value={dataToSave.cse_gate_min || ""} onChange={(e) => setDataToSave({ ...dataToSave, cse_gate_min: e.target.value })} />
                    </div>
                    <div className="col-md-3">
                      <input type="number" className="form-control" placeholder="Max Score" value={dataToSave.cse_gate_max || ""} onChange={(e) => setDataToSave({ ...dataToSave, cse_gate_max: e.target.value })} />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-3 fw-bold d-flex align-items-center">M.Tech ECE <span className="badge bg-primary ms-2">{gateCounts.ece_count ?? 0} Applications</span></div>
                    <div className="col-md-3">
                      <input type="number" className="form-control" placeholder="Min Score" value={dataToSave.ece_gate_min || ""} onChange={(e) => setDataToSave({ ...dataToSave, ece_gate_min: e.target.value })} />
                    </div>
                    <div className="col-md-3">
                      <input type="number" className="form-control" placeholder="Max Score" value={dataToSave.ece_gate_max || ""} onChange={(e) => setDataToSave({ ...dataToSave, ece_gate_max: e.target.value })} />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-3 fw-bold d-flex align-items-center">M.Tech AI&DS <span className="badge bg-primary ms-2">{gateCounts.ai_count ?? 0} Applications</span></div>
                    <div className="col-md-3">
                      <input type="number" className="form-control" placeholder="Min Score" value={dataToSave.ai_gate_min || ""} onChange={(e) => setDataToSave({ ...dataToSave, ai_gate_min: e.target.value })} />
                    </div>
                    <div className="col-md-3">
                      <input type="number" className="form-control" placeholder="Max Score" value={dataToSave.ai_gate_max || ""} onChange={(e) => setDataToSave({ ...dataToSave, ai_gate_max: e.target.value })} />
                    </div>
                  </div>
                </>
              )}

              {/* ── Previous round selection ──────────────────────────────── */}
              {!isOnline && (
                <>
                  <hr className="my-3" />
                  <div className="mb-2">
                    <label className="fw-semibold mb-1 d-block">
                      Select candidates from previous round
                      <span className="text-muted small ms-2">
                        (leave blank to consider all eligible candidates)
                      </span>
                    </label>

                    {/* Mode selector */}
                <div className="d-flex align-items-center gap-3 mb-2">
                  <select
                    className="form-select"
                    style={{ maxWidth: 160 }}
                    value={prevRoundMode}
                    onChange={(e) => setPrevRoundMode(e.target.value as "INCLUDE" | "EXCLUDE")}
                    disabled={prevRoundOptions.length === 0}
                  >
                    <option value="INCLUDE">Include</option>
                    <option value="EXCLUDE">Exclude</option>
                  </select>
                  <select
                    className="form-select"
                    style={{ maxWidth: 380 }}
                    value={prevRoundId}
                    onChange={(e) => setPrevRoundId(e.target.value)}
                    disabled={prevRoundOptions.length === 0}
                  >
                    <option value="">All eligible candidates (no filter) </option>
                    {prevRoundOptions.map((r: any) => (
                      <option key={r.id} value={r.id}>
                        {r.name || r.id} ({formatDateDDMMYYYY(r.startdate || r.date)})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {prevRoundId && (
                <div className="mb-3">
                  <label className="fw-semibold mb-1 d-block">
                    {prevRoundMode === "INCLUDE" ? "Include" : "Exclude"} candidates with offer status:
                  </label>
                  <div className="d-flex flex-wrap gap-3">
                    {/* All checkbox */}
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="create-status-ALL"
                        checked={prevStatuses.length === ALL_PREV_STATUS_VALUES.length}
                        ref={(el) => {
                          if (el) el.indeterminate = prevStatuses.length > 0 && prevStatuses.length < ALL_PREV_STATUS_VALUES.length;
                        }}
                        onChange={toggleAllPrevStatuses}
                      />
                      <label className="form-check-label fw-semibold" htmlFor="create-status-ALL">All</label>
                    </div>
                    {PREV_STATUS_OPTIONS.map(({ label, value }) => (
                      <div className="form-check" key={value}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`create-status-${value}`}
                          checked={prevStatuses.includes(value)}
                          onChange={() => togglePrevStatus(value)}
                        />
                        <label className="form-check-label" htmlFor={`create-status-${value}`}>
                          {label}
                        </label>
                      </div>
                    ))}
                  </div>
                  {prevStatuses.length === 0 && (
                    <div className="text-danger small mt-1">
                      Select at least one status to filter candidates.
                    </div>
                  )}
                </div>
              )}
              </>
              )}

              {!isOnline && (
                <div className="row mt-3">
                  <div className="col-md-12 text-end">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={!!prevRoundId && prevStatuses.length === 0}
                      onClick={fetchCounts}
                    >
                      Generate Cutoffs
                      {/* &amp; Counts */}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {(isBtech && isOnline) && (
            <>
              <div className="col-12 mt-3 p-3 bg-light border rounded" style={{ gridColumn: "1 / -1" }}>
                <h5 className="mb-3">Allocation Strategy</h5>

                <div className="mb-4">
                  <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" name="allocation_type" id="allocSeat" value="SEAT_BASED"
                      checked={dataToSave.allocation_type !== "CUTOFF_BASED"}
                      onChange={() => setDataToSave({ ...dataToSave, allocation_type: "SEAT_BASED", cse_gate_min: "", cse_gate_max: "", ece_gate_min: "", ece_gate_max: "", ai_gate_min: "", ai_gate_max: "" })} />
                    <label className="form-check-label" htmlFor="allocSeat">Seat Based Allocation</label>
                  </div>
                  {/* <div className="form-check form-check-inline">
                  <input className="form-check-input" type="radio" name="allocation_type" id="allocCutoff" value="CUTOFF_BASED"
                    checked={dataToSave.allocation_type === "CUTOFF_BASED"}
                    onChange={() => setDataToSave({ ...dataToSave, allocation_type: "CUTOFF_BASED", cse_seats: "", ece_seats: "", ai_seats: "" })} />
                  <label className="form-check-label" htmlFor="allocCutoff">Cutoff Range Based Allocation</label>
                </div> */}
                </div>

                {dataToSave.allocation_type !== "CUTOFF_BASED" ? (
                  <>
                    {/* Seat rows */}
                    {[
                      { label: "B.Tech CSE", field: "btech_online_cse_seats" },
                      { label: "B.Tech ECE", field: "btech_online_ece_seats" },
                      { label: "B.Tech AI&DS", field: "btech_online_aids_seats" },
                      { label: "Integrated M.Tech CSE", field: "imtech_online_cse_seats" },
                      { label: "Integrated M.Tech ECE", field: "imtech_online_ece_seats" },
                    ].map(({ label, field }) => (
                      <div className="row mb-3" key={field}>
                        <div className="col-md-2 fw-bold d-flex align-items-center">{label}</div>
                        <div className="col-md-4">
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Available Seats"
                            value={(dataToSave as any)[field] || ""}
                            onChange={(e) => setDataToSave({ ...dataToSave, [field]: e.target.value })}
                          />
                        </div>
                        {/* <div className="col-md-6 d-flex align-items-center gap-2 text-primary">
                        {gateCounts[cutoffKey] != null && (
                          <span className="badge bg-success">Cutoff: {gateCounts[cutoffKey]}</span>
                        )}
                        {gateCounts[countKey] > 0 && (
                          <span>Allocating {gateCounts[countKey]} candidates</span>
                        )}
                      </div> */}
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    {/* Cutoff Range Rows */}
                    <div className="row mb-3">
                      <div className="col-md-3 fw-bold d-flex align-items-center">M.Tech CSE <span className="badge bg-primary ms-2">{gateCounts.cse_count ?? 0} Applications</span></div>
                      <div className="col-md-3">
                        <input type="number" className="form-control" placeholder="Min Score" value={dataToSave.cse_gate_min || ""} onChange={(e) => setDataToSave({ ...dataToSave, cse_gate_min: e.target.value })} />
                      </div>
                      <div className="col-md-3">
                        <input type="number" className="form-control" placeholder="Max Score" value={dataToSave.cse_gate_max || ""} onChange={(e) => setDataToSave({ ...dataToSave, cse_gate_max: e.target.value })} />
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-3 fw-bold d-flex align-items-center">M.Tech ECE <span className="badge bg-primary ms-2">{gateCounts.ece_count ?? 0} Applications</span></div>
                      <div className="col-md-3">
                        <input type="number" className="form-control" placeholder="Min Score" value={dataToSave.ece_gate_min || ""} onChange={(e) => setDataToSave({ ...dataToSave, ece_gate_min: e.target.value })} />
                      </div>
                      <div className="col-md-3">
                        <input type="number" className="form-control" placeholder="Max Score" value={dataToSave.ece_gate_max || ""} onChange={(e) => setDataToSave({ ...dataToSave, ece_gate_max: e.target.value })} />
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-3 fw-bold d-flex align-items-center">M.Tech AI&DS <span className="badge bg-primary ms-2">{gateCounts.ai_count ?? 0} Applications</span></div>
                      <div className="col-md-3">
                        <input type="number" className="form-control" placeholder="Min Score" value={dataToSave.ai_gate_min || ""} onChange={(e) => setDataToSave({ ...dataToSave, ai_gate_min: e.target.value })} />
                      </div>
                      <div className="col-md-3">
                        <input type="number" className="form-control" placeholder="Max Score" value={dataToSave.ai_gate_max || ""} onChange={(e) => setDataToSave({ ...dataToSave, ai_gate_max: e.target.value })} />
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="col-12 mt-3 p-3 bg-light border rounded" style={{ gridColumn: "1 / -1" }}>
                <h5 className="mb-3">Cut Off</h5>

                <div>
                  <div className={styles.formGroup}>
                    <input
                      className={styles.formControl}
                      value={dataToSave.online_cutOff || ""}
                      onChange={(e) => setDataToSave({
                        ...dataToSave, online_cutOff: e.target.value,
                        btech_cse_cutoff: e.target.value,
                        btech_ece_cutoff: e.target.value,
                        btech_ai_cutoff: e.target.value,
                        imtech_cse_cutoff: e.target.value,
                        imtech_ece_cutoff: e.target.value,
                      })}
                      placeholder="Enter the CutOff for Online Round"
                    />
                  </div>
                </div>
              </div>
            </>
          )

          }

          {/* ── B.Tech section ─────────────────────────────────────────────── */}
          {isBtech && dataToSave.round_type?.toUpperCase() === "OFFLINE" && (
            <div className="col-12 mt-3 p-3 bg-light border rounded" style={{ gridColumn: "1 / -1" }}>

              {/* Header row: title + Round Mode radios */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Allocation Strategy</h5>
                <div className="d-flex align-items-center gap-3">
                  <span className="fw-semibold small text-muted">Round Mode:</span>
                  <div className="form-check form-check-inline mb-0">
                    <input className="form-check-input" type="radio" id="modeFresh" name="allocationMode"
                      checked={allocationMode === ""}
                      onChange={() => { setAllocationMode(""); setParentRoundId(""); }} />
                    <label className="form-check-label" htmlFor="modeFresh">Fresh Round</label>
                  </div>
                  <div className="form-check form-check-inline mb-0">
                    <input className="form-check-input" type="radio" id="modeRecovery" name="allocationMode"
                      checked={allocationMode === "RECOVERY"}
                      onChange={() => setAllocationMode("RECOVERY")} />
                    <label className="form-check-label" htmlFor="modeRecovery">Revised Allocation Round (1.x, 2.x etc)</label>
                  </div>
                  <div className="form-check form-check-inline mb-0">
                    <input className="form-check-input" type="radio" id="modeSliding" name="allocationMode"
                      checked={allocationMode === "SLIDING"}
                      onChange={() => setAllocationMode("SLIDING")} />
                    <label className="form-check-label" htmlFor="modeSliding">Sliding Round</label>
                  </div>
                </div>
              </div>

              {/* Seat rows */}
              <>
                {allocationMode === "SLIDING" && (
                  <div className="row mb-2 mt-4">
                    <div className="col-md-3"></div>
                    <div className="col-md-3 fw-bold text-muted" style={{ fontSize: "0.85em", textTransform: "uppercase" }}>Total Target Capacity</div>
                    <div className="col-md-6"></div>
                  </div>
                )}
                {[
                  { label: "B.Tech CSE", field: "btech_cse_seats", countKey: "btech_cse_count", cutoffKey: "btech_cse_cutoff", effKey: "btech_cse_seats" },
                  { label: "B.Tech ECE", field: "btech_ece_seats", countKey: "btech_ece_count", cutoffKey: "btech_ece_cutoff", effKey: "btech_ece_seats" },
                  { label: "B.Tech AI&DS", field: "btech_ai_seats", countKey: "btech_ai_count", cutoffKey: "btech_ai_cutoff", effKey: "btech_ai_seats" },
                  { label: "IM.Tech CSE", field: "imtech_cse_seats", countKey: "imtech_cse_count", cutoffKey: "imtech_cse_cutoff", effKey: "imtech_cse_seats" },
                  { label: "IM.Tech ECE", field: "imtech_ece_seats", countKey: "imtech_ece_count", cutoffKey: "imtech_ece_cutoff", effKey: "imtech_ece_seats" },
                ].map(({ label, field, countKey, cutoffKey, effKey }) => (
                  <div className="row mb-3" key={field}>
                    <div className="col-md-3 fw-bold d-flex align-items-center">{label}</div>
                    <div className="col-md-3">
                      <input
                        type="number"
                        className="form-control"
                        placeholder={allocationMode === "RECOVERY" ? "Auto-calculated" : allocationMode === "SLIDING" ? "Total Seats" : "Configured Seats"}
                        value={(dataToSave as any)[field] || ""}
                        onChange={(e) => setDataToSave({ ...dataToSave, [field]: e.target.value })}
                        disabled={allocationMode === "RECOVERY"}
                      />
                    </div>
                    <div className="col-md-6 d-flex align-items-center gap-2">
                      {gateCounts[cutoffKey] != null && (
                        <span className="badge bg-success">Cutoff: {gateCounts[cutoffKey]}</span>
                      )}
                      {(gateCounts[countKey] ?? 0) > 0 && (
                        <span className="text-primary">
                          Allocating {(gateCounts[countKey] ?? 0) - (gateCounts?.extra_data?.[countKey.replace("_count", "_retained")] ?? gateCounts?.[countKey.replace("_count", "_retained")] ?? 0)} candidates
                          {allocationMode === "SLIDING" && (
                            <span className="ms-1" style={{ fontSize: "0.85em", color: "#6c757d" }}>
                              ({gateCounts?.extra_data?.[countKey.replace("_count", "_fresh")] ?? gateCounts?.[countKey.replace("_count", "_fresh")] ?? 0} FRESH / {gateCounts?.extra_data?.[countKey.replace("_count", "_paid_slide")] ?? gateCounts?.[countKey.replace("_count", "_paid_slide")] ?? 0} PAID SLIDE / {gateCounts?.extra_data?.[countKey.replace("_count", "_slide")] ?? gateCounts?.[countKey.replace("_count", "_slide")] ?? 0} SLIDE / {gateCounts?.extra_data?.[countKey.replace("_count", "_retained")] ?? gateCounts?.[countKey.replace("_count", "_retained")] ?? 0} RETAINED)
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </>

              <hr className="my-3" />

              {/* RECOVERY/SLIDING mode: parent round selector */}
              {(allocationMode === "RECOVERY" || allocationMode === "SLIDING") && (
                <>
                  <div className="mb-2">
                    <label className="fw-semibold mb-1 d-block">Parent Round</label>
                    <select
                      className="form-select"
                      style={{ maxWidth: 380 }}
                      value={parentRoundId}
                      onChange={(e) => setParentRoundId(e.target.value)}
                    >
                      <option value="">Select parent round…</option>
                      {prevRoundOptions.map((r: any) => (
                        <option key={r.id} value={r.id}>
                          {r.name || r.id} ({formatDateDDMMYYYY(r.startdate || r.date)})
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {/* FRESH mode: standard prev-round selector (optional advanced use) */}
              {allocationMode === "" && (
                <>
                  <div className="mb-2">
                    <label className="fw-semibold mb-1 d-block">
                      Select candidates from previous round
                      <span className="text-muted small ms-2">(leave blank to consider all eligible candidates)</span>
                    </label>
                    <div className="d-flex align-items-center gap-3 mb-2">
                      <select className="form-select" style={{ maxWidth: 160 }}
                        value={prevRoundMode}
                        onChange={(e) => setPrevRoundMode(e.target.value as "INCLUDE" | "EXCLUDE")}>
                        <option value="INCLUDE">Include</option>
                        <option value="EXCLUDE">Exclude</option>
                      </select>
                      <select className="form-select" style={{ maxWidth: 380 }}
                        value={prevRoundId}
                        onChange={(e) => setPrevRoundId(e.target.value)}>
                        <option value="">All eligible candidates (no filter)</option>
                        {prevRoundOptions.map((r: any) => (
                          <option key={r.id} value={r.id}>
                            {r.name || r.id} ({formatDateDDMMYYYY(r.startdate || r.date)})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {prevRoundId && (
                    <div className="mb-3">
                      <label className="fw-semibold mb-1 d-block">
                        {prevRoundMode === "INCLUDE" ? "Include" : "Exclude"} candidates with offer status:
                      </label>
                      <div className="d-flex flex-wrap gap-3">
                        <div className="form-check">
                          <input className="form-check-input" type="checkbox" id="create-status-ALL"
                            checked={prevStatuses.length === ALL_PREV_STATUS_VALUES.length}
                            ref={(el) => { if (el) el.indeterminate = prevStatuses.length > 0 && prevStatuses.length < ALL_PREV_STATUS_VALUES.length; }}
                            onChange={toggleAllPrevStatuses} />
                          <label className="form-check-label fw-semibold" htmlFor="create-status-ALL">All</label>
                        </div>
                        {PREV_STATUS_OPTIONS.map(({ label, value }) => (
                          <div className="form-check" key={value}>
                            <input className="form-check-input" type="checkbox" id={`create-status-${value}`}
                              checked={prevStatuses.includes(value)}
                              onChange={() => togglePrevStatus(value)} />
                            <label className="form-check-label" htmlFor={`create-status-${value}`}>{label}</label>
                          </div>
                        ))}
                      </div>
                      {prevStatuses.length === 0 && (
                        <div className="text-danger small mt-1">Select at least one status to filter candidates.</div>
                      )}
                    </div>
                  )}
                </>
              )}

              <div className="row mt-3">
                <div className="col-md-12 text-end">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={allocationMode === "RECOVERY" || allocationMode === "SLIDING" ? !parentRoundId : (!!prevRoundId && prevStatuses.length === 0)}
                    onClick={fetchCounts}
                  >
                    Generate Cutoffs
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Offer Letter Template */}
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>
            <span className={styles.required}>*</span> Offer Letter Template
          </label>
          <input
            type="file"
            accept=".docx"
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              if (file) {
                setDataToSave({ ...dataToSave, offer_letter_template_doc_id: file });
              } else {
                const newData = { ...dataToSave };
                delete newData.offer_letter_template_doc_id;
                setDataToSave(newData);
              }
            }}
          />
          {typeof dataToSave?.offer_letter_template_doc_id === "string" &&
            dataToSave?.offer_letter_template_doc_id?.trim() !== "" && (
              <div style={{ marginTop: "8px" }}>
                <DocumentPreview
                  docId={dataToSave.offer_letter_template_doc_id}
                  label="Download Current Template (.docx)"
                />
              </div>
            )}
        </div>


        {/* Manual Round */}

        {isManual && isBtech &&
          <>
            {dataToSave.drive_id &&
              <button style={{ margin: 20 }} className={styles.primaryBtn} onClick={() => fetchApplications(dataToSave.drive_id)}>Get Applications</button>}

            {loading ?
              <div>Loading....</div> :
              applications?.length > 0 ?
                <div
                  className="ag-theme-alpine"
                  style={{ width: "100%", height: 600 }}
                >
                  <AgGridReact
                    rowData={applications}
                    columnDefs={columnDefs}
                    pagination={true}
                    paginationPageSize={10}
                    defaultColDef={{
                      sortable: true,
                      filter: true,
                      resizable: true,
                      flex: 1,
                    }}
                    suppressRowClickSelection
                    rowSelection="multiple"
                    getRowId={(params) => params.data.id}
                    rowHeight={48}
                  />
                </div> : <div> No application found</div>}
          </>
        }

        <div className={styles.buttonRow}>
          <button className={styles.primaryBtn} onClick={handleCreate}>Submit</button>
        </div>
      </div >
      {/* Toast */}
      {
        toast && (
          <div className="toast-container position-fixed top-0 end-0 p-3 mt-5" style={{ zIndex: 1060 }}>
            <div className="toast show">
              <div className="toast-header">
                <strong className="me-auto">{toast.type === "success" ? "Success" : "Error"}</strong>
                <button className="btn-close" onClick={() => setToast(null)} />
              </div>
              <div className={`toast-body text-center ${toast.type === "success" ? "text-success" : "text-danger"}`}>
                {toast.msg}
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default CreateRounds;
